import { useEffect, useRef, useState } from 'react';
import { Formik, FormikErrors, FormikProps } from 'formik';
import { Button } from '@pmi/dsm-react';
import { Spinner } from 'react-bootstrap';
import Overlay from '../../Overlay';
import useSchemaValidator from '../../../hooks/useSchemaValidator';
import PhoneNumberForm from '../../PhoneNumberForm';
import { PhoneNumber } from '../../../models/ProfileModel';
import {
  useAddPhoneNumberMutation,
  useGetPhoneNumberQuery,
  useUpdatePhoneNumberMutation,
} from '../apiSlice';
import { Option } from '../../../../Admin/People/TabsCard/FilterBy';
import { toastWithAnalytics } from '../../../analytics';
import { PhoneNumberInput, SelectOption } from '../../PhoneNumberForm/models';

export interface ContactProps {
  onPhoneNumberSelected: (
    phoneNumber: PhoneNumber,
    countries: SelectOption[],
    init: boolean,
  ) => void;
  onClose: () => void;
}

export default function Contact({
  onPhoneNumberSelected,
  onClose,
}: ContactProps) {
  const { data, isLoading, isFetching } = useGetPhoneNumberQuery(null);
  const [isSubmittingPhone, setIsSubmittingPhone] = useState(false);

  const [countries, setCountries] = useState<SelectOption[]>([]);

  const formikRef = useRef<FormikProps<PhoneNumberInput>>(undefined);
  const { validatePhoneNumber } = useSchemaValidator();
  const [updatePhoneNumber, { isLoading: updatePhoneNumberLoading }] =
    useUpdatePhoneNumberMutation();
  const [addPhoneNumber, { isLoading: addPhoneNumberLoading }] =
    useAddPhoneNumberMutation();

  const emptyPhone = {
    countryId: null as number | null,
    number: '',
    extension: '',
    primary: true,
    type: 1,
    id: 0,
  };

  const [currentPhoneNumber, setCurrentPhoneNumber] =
    useState<PhoneNumber>(null);

  const validateInput = (values: PhoneNumberInput) => {
    const errors: FormikErrors<PhoneNumberInput> = {};

    try {
      const result = validatePhoneNumber(
        values.phoneNumber,
        data?._embedded?.schema,
      );
      if (!result.valid) {
        errors.phoneNumber = result.errors;
        setIsSubmittingPhone(false);
      }
    } catch (error) {
      toastWithAnalytics('Unexpected Error while validating data', 'danger');
      setIsSubmittingPhone(false);
    }

    setTimeout(() => {
      const invalids = document.querySelectorAll(
        '.mdlProfileUpdate .is-invalid',
      );
      if (invalids.length > 0) {
        invalids.item(0).scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);

    return errors;
  };

  useEffect(() => {
    if (!isLoading && data && data?._embedded.countries && !isFetching) {
      const options: Option[] = [];
      data?._embedded.countries.map((c) =>
        options.push({ label: c.text, value: c.value } as Option),
      );
      if (data.items) {
        const selected = data.items[0];
        if (selected) {
          setCurrentPhoneNumber(selected);
          onPhoneNumberSelected(selected, data?._embedded.countries, true);
        } else {
          setCurrentPhoneNumber(emptyPhone);
        }
      }
      setCountries(data?._embedded.countries);
    }
  }, [isLoading, isFetching, data]);

  const submitPhoneNumber = async (values: PhoneNumberInput) => {
    const { phoneNumber } = values;

    const mutation = phoneNumber.id > 0 ? updatePhoneNumber : addPhoneNumber;

    const resPhoneNumber = await mutation(phoneNumber).unwrap();

    setCurrentPhoneNumber(resPhoneNumber);
    setIsSubmittingPhone(false);
    onPhoneNumberSelected(resPhoneNumber, countries, false);
  };

  const onSavePhoneNumber = async () => {
    setIsSubmittingPhone(true);

    try {
      await formikRef.current.submitForm();
    } catch (error) {
      setIsSubmittingPhone(false);
      toastWithAnalytics(
        'There was an unexpected error while updating the cart phone number',
        'danger',
      );
    }
  };

  if (isLoading || isFetching || !currentPhoneNumber) {
    return (
      <div className="text-center mb-4 typography16 gray600">
        <Spinner size="sm" /> Loading...
      </div>
    );
  }

  return (
    <>
      <div>
        <Formik
          initialValues={{ phoneNumber: currentPhoneNumber }}
          onSubmit={submitPhoneNumber}
          validate={validateInput}
          validateOnBlur={false}
          validateOnChange={false}
          innerRef={formikRef}
        >
          <div>
            <div className="d-block">
              <PhoneNumberForm
                countries={countries}
                title={
                  currentPhoneNumber?.id
                    ? 'Edit Phone Number'
                    : 'Add Phone Number'
                }
                showExtension={false}
              />
            </div>
            <div className="d-block">
              <div className="d-flex justify-content-end align-items-center align-content-center gap-3">
                <Button
                  titleText="Cancel"
                  type="button"
                  variant="outline-secondary"
                  disabled={isSubmittingPhone}
                  size="sm"
                  onClick={() => onClose()}
                />
                <Button
                  titleText="Save Phone Number"
                  disabled={isSubmittingPhone}
                  onClick={onSavePhoneNumber}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </Formik>
      </div>
      <div>
        <Overlay
          variant="PMIOverlay"
          global
          animated
          fade
          iconColor="brand-dark"
          overlayColor="black"
          overlayOpacity={1}
          show={updatePhoneNumberLoading || addPhoneNumberLoading}
        />
      </div>
    </>
  );
}
